import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'

import TanStackQueryDevtools from '../integrations/tanstack-query/devtools'

import appCss from '../styles.css?url'
import { THEME_INIT_SCRIPT } from '../lib/theme.ts'
import { AppShell } from '../components/AppShell.tsx'
import { RouteError, RoutePending } from '../components/RouteBoundary.tsx'
import {
  TENANT_PRODUCTS_STALE_MS,
  loadNavProducts,
} from '../lib/tenant-products.ts'

import type { QueryClient } from '@tanstack/react-query'

interface MyRouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Stoaria Control',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
    ],
  }),
  /**
   * The tenant's product entitlement, loaded once for the whole app.
   *
   * It belongs on the root because the CHROME needs it and the chrome is
   * the root's component: a sidebar that fetched for itself would render
   * the full nav and then drop a section on every page load. The root is
   * also the only match guaranteed to be present, so there is nowhere
   * lower to put it.
   *
   * Never throws — `loadNavProducts` turns a failure (including the 401
   * on `/login`, where there is no tenant at all) into `null`, meaning
   * "unknown", which renders the whole nav. A throwing root loader would
   * replace the entire app with the error boundary over a cosmetic
   * filter.
   *
   * `staleTime` here is what stops the loader re-running on every
   * navigation; the query cache behind it would absorb the repeat
   * anyway, but a pending root match flashes `RoutePending` over the
   * chrome while it resolves.
   */
  loader: async ({ context }) => ({
    products: await loadNavProducts(context.queryClient),
  }),
  staleTime: TENANT_PRODUCTS_STALE_MS,
  // Three separate slots, and the split matters. `shellComponent` is the
  // DOCUMENT (<html>, the pre-paint theme script, the script tags);
  // `component` is the CHROME, rendered inside the root match; the error
  // and pending components replace the chrome when the match itself is
  // broken or still loading. The old root had only the first and did all
  // three jobs in it.
  component: RootLayout,
  errorComponent: RouteError,
  pendingComponent: RoutePending,
  shellComponent: RootDocument,
})

/** Every page in the app renders inside this. The root route is the only
 * place that can own the chrome, because it is the only match that is
 * always present. */
function RootLayout() {
  const { products } = Route.useLoaderData()
  return (
    <AppShell products={products}>
      <Outlet />
    </AppShell>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    // The init script below writes `data-theme` onto this element before
    // React hydrates, so React is told not to report the attribute it did
    // not render as a mismatch. See lib/theme.ts.
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* First thing in <head>, and deliberately blocking: it resolves
            the theme before the browser paints anything, which is what
            stops a light-theme operator getting a black flash on every
            full document load. */}
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        <HeadContent />
      </head>
      <body>
        {children}
        {/* Dev only. This shipped to production: the devtools panel's
            floating trigger sat on every page of the live admin, and the
            router/query panels it opens expose the full route tree and
            every cached API response to anyone who clicks it. Vite
            statically replaces `import.meta.env.DEV` with `false` in a
            build, so the whole subtree is dropped from the bundle rather
            than merely hidden. */}
        {import.meta.env.DEV && (
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
              TanStackQueryDevtools,
            ]}
          />
        )}
        <Scripts />
      </body>
    </html>
  )
}
